import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, Clock, Film } from "lucide-react";
import Header from "../components/Header";

const API = "/api";

export default function LandingPage() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${API}/series`);
        setSeries(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load series");
      } finally {
        setLoading(false);
      }
    };

    fetchSeries();
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  const placeholderImages = [
    "https://images.unsplash.com/photo-1639749601642-8d30474f249f?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1566750859749-5aa92e0b4872?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1766153550988-121007d31002?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1759911706620-a685c003aa3b?w=600&h=400&fit=crop",
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <div className="p-4 md:p-8 lg:p-12">
        <div className="mb-8">
          <h1 data-testid="app-title" className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tighter mb-2">
            Mes Chaînes
          </h1>
          <p className="text-white/60 text-base">Sélectionnez une chaîne pour commencer</p>
        </div>

        {loading ? (
          <div data-testid="loading-spinner" className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div data-testid="error-message" className="border border-red-500/30 bg-red-500/10 p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : series.length === 0 ? (
          <div data-testid="empty-state" className="border border-white/10 bg-white/5 p-12 text-center">
            <Film className="w-16 h-16 mx-auto mb-4 text-white/30" />
            <p className="text-white/60 text-lg">No channels available</p>
            <p className="text-white/40 text-sm mt-2">Add video folders to /videos/ to create channels</p>
          </div>
        ) : (
          <div data-testid="series-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {series.map((item, index) => (
              <Link
                key={item.name}
                to={`/watch/${item.name}`}
                data-testid={`series-card-${item.name}`}
                className="group relative border border-white/10 bg-[#0a0a0a] overflow-hidden transition-all duration-300 hover:border-white/30"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={item.cover_image || placeholderImages[index % placeholderImages.length]}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 px-2 py-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse-live" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Live</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-8 h-8 text-white fill-white" />
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-white uppercase tracking-tight mb-2">
                    {item.name.replace(/_/g, " ")}
                  </h2>
                  <div className="flex items-center gap-4 text-white/50 text-sm">
                    <span className="flex items-center gap-1"><Film className="w-4 h-4" />{item.video_count} videos</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDuration(item.total_duration)} cycle</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
